import { useState, type ReactNode } from "react";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import {
  useLibraryAssetsQuery,
  useLibraryPlacementRegistryQuery,
  useProjectAdoptionsQuery,
  type LibraryAsset,
} from "@/api/library";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import {
  activationLaneForSection,
  adoptionLock,
  adoptionRefusal,
  type AdoptionDecision,
} from "@/lib/adoptions";
import { sectionAdoptionCandidates } from "@/lib/sectionAdoption";
import { Badge } from "@/ui/Badge";
import { Notice } from "@/ui/Notice";
import { ProjectSectionPane } from "@/ui/project/ProjectSectionPane";
import { EmptyState, StaleDataNotice } from "@/ui/states";

import { AdoptionDecisionDialog } from "@/ui/adoptions/AdoptionDecisionDialog";
import { AdoptFromLibraryAction } from "@/ui/adoptions/AdoptFromLibraryAction";
import { AdoptedRow } from "@/ui/adoptions/AdoptionRow";

/**
 * The Closing conversation destination in the Clinical configuration rail
 * (#867).
 *
 * ## WHY THIS EXISTS RATHER THAN AN EIGHTH SECTION
 *
 * `closing` is NOT a member of the contract's `SectionSlug`, and
 * `SectionSurface`'s guard says outright that sending a rail slug to
 * `getProjectSection` on the strength of its name "is the mistake this guard
 * exists to make impossible". The S7 screen contract asks for a Project entry
 * "after Questionnaires, before Summary" and forbids inventing an unsupported
 * slug, so this surface is reached by the same slug-specific mechanism
 * `questionnaires` already uses: the rail entry declares `projection: "none"`,
 * `SectionSurface` routes it here, and `isSectionSlug()` keeps gating the
 * projection fetch for everything else. No `SectionSlug` member was added and no
 * endpoint was invented.
 *
 * ## IT IS NOT A DIAGNOSTIC SELECTION, AND THE SCREEN SAYS SO
 *
 * The contract is explicit — "Never count as diagnostic Always/Dynamic" — and
 * that is a runtime fact rather than a presentation preference: the closing agent
 * is the terminal node the engine runs AFTER every final track, it takes no part
 * in `dynamic_domains` selection, and it counts against no Dynamic cap. So this
 * destination is its own rail entry rather than a group on Project Pathways, it
 * offers no Always-assessed switch, and it states the stage placement in words.
 * Nothing here reads or writes routing or Safety state.
 *
 * ## ENABLED IS A PIN, AND REMOVING IT IS THE DISABLE
 *
 * A project's closing conversation is one exact immutable pin or none. Adopting
 * one wires the runtime's two closing enabler keys; removing it reverts them, so
 * the project inherits the base pack's own closing configuration — which for the
 * base pack is no closing node at all, and the session completes straight after
 * the final tracks. Both writes are the Editor Service's `closing-activation`,
 * reached through the SAME `AdoptionDecisionDialog` every other family uses, so
 * there is still exactly one route into changing an adoption and the sentence a
 * clinician confirms is the one the server decided.
 *
 * Restoring a removed closing conversation is adopting it again: versions are
 * immutable, so the exact version that was removed is still in the shared
 * library and pinning it back restores what was running. An ARCHIVED asset is
 * restored on its library page, by the administrator capability the service
 * reserves for it; this surface states that refusal on the row rather than
 * hiding the asset.
 *
 * ## NO SECOND PIN SET, AND NO SECOND WRITE PATH
 *
 * The pin set and the catalog are read through the same hooks — and therefore
 * the same cache entries — as every other adoption surface, so a clinician
 * moving between sections observes one pin set rather than several.
 */
export function ProjectClosingConversation({
  projectId,
  sectionLabel = "Closing conversation",
  controls,
}: {
  projectId: string;
  /**
   * The section's own name from the rail, for the pane's accessible heading
   * (#909). Defaulted: this component serves exactly one rail slug.
   */
  sectionLabel?: string;
  /**
   * Section-scoped controls above the rows, or absent (#909).
   *
   * `ProjectSectionPane`'s stable insertion point, so a later leaf adds a
   * control here the same way it does on every other Project section.
   */
  controls?: ReactNode;
}) {
  const adoptions = useProjectAdoptionsQuery(projectId);
  const catalog = useLibraryAssetsQuery(true);
  const write = useCapability("draft.write");
  // The SERVED cardinality policy. This family's bound is one adopted pin, and
  // `AdoptionDecisionDialog` cannot describe which pin a second one displaces
  // without this document — so an unread policy becomes a STATED refusal on the
  // adopt control rather than a control whose only outcome is that refusal
  // (ADR 0001). Removal is deliberately not gated on it: an un-pin is the
  // recovery path out of every degraded state (#612), and here it is also the
  // only way to disable a closing conversation.
  const policyUnread = !useLibraryPlacementRegistryQuery().isSuccess;
  const [decision, setDecision] = useState<AdoptionDecision | null>(null);

  // Derived above the branches below so the hook's argument never depends on
  // which branch renders. A conditional call is a conditional hook.
  const terminalCatalogFailure =
    catalog.isError && isTerminalQueryError(catalog.error);
  // A transient failed refresh may keep the last authorized response visible,
  // with an explicit stale-data notice. A terminal answer (especially 401/403)
  // must fail closed even when TanStack Query still carries an older response.
  const readableCatalog = terminalCatalogFailure ? undefined : catalog.data;
  const assetsById = new Map<string, LibraryAsset>(
    (readableCatalog?.items ?? []).map((asset) => [asset.id, asset]),
  );
  const pins = adoptions.data?.items ?? [];

  // THE SHELL SURVIVES A DEGRADED READ (#909 round 2). A bare query state here
  // would take the section's accessible heading — and anything a later leaf
  // supplied through `controls` — off the screen while the pin set loads or
  // after it fails. What a degraded read removes is the ADOPTION ACTION,
  // because the pin set is not current and no write could be described.
  if (adoptions.isPending) {
    return (
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        {...(controls ? { controls } : {})}
      >
        <QueryPendingState
          isPaused={adoptions.isPaused}
          failureReason={adoptions.failureReason}
          label="Loading this project's closing conversation"
          errorTitle="This project's closing conversation could not be loaded"
          rows={2}
          queryKey={queryKeys.projectAdoptions(projectId)}
        />
      </ProjectSectionPane>
    );
  }

  if (adoptions.isError) {
    return (
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        {...(controls ? { controls } : {})}
      >
        <QueryErrorState
          error={adoptions.error}
          subject="Closing conversation"
          title="This project's closing conversation could not be loaded"
          description="The Editor Service did not return this project's pins, so whether a closing conversation is enabled is not known here."
          onRetry={() => void adoptions.refetch()}
        />
      </ProjectSectionPane>
    );
  }

  // THE PIN SET ANSWERED; THE CATALOG MAY NOT HAVE (#615). A failed catalog read
  // must not replace this surface with one error: while the exact pin is known,
  // the way back to its authoritative library asset stays. Each pin carries its
  // own family (`assetType`, joined server-side), so the row below can be listed
  // and addressed from the pin set alone.
  const catalogNotice = catalog.isPending ? (
    <Notice>
      The shared library catalog is still loading. A stored closing pin remains
      listed below by id, exact version and exact library page; its name,
      library-head comparison and adoption controls appear after the catalog
      answers.
    </Notice>
  ) : catalog.isError && catalog.data && !terminalCatalogFailure ? (
    <StaleDataNotice
      updatedAt={catalog.dataUpdatedAt}
      onRetry={() => void catalog.refetch()}
    />
  ) : catalog.isError ? (
    <QueryErrorState
      error={catalog.error}
      subject="Library assets"
      title="The pinned closing conversation could not be named"
      description="The shared library catalog that names this project's pins did not load. The pin below is listed by its stored identity — the exact version and its library page — without its name or its library head, and nothing further can be adopted until the catalog answers."
      onRetry={() => void catalog.refetch()}
    />
  ) : null;

  // The confirmation dialog deliberately requires a current successful catalog
  // read. Keep the row and its exact read-only link visible while that read is
  // unsettled, but do not offer a control the dialog cannot execute.
  const catalogDecisionRefusal = catalog.isSuccess
    ? null
    : "The shared library catalog must answer before this adoption can change.";

  // Which pin is the closing conversation is the PIN SET's own fact (#615): the
  // service joins `assetType` onto each stored pin, so a pin the catalog did not
  // name is still listed here as what it is.
  const closingPins = pins.filter(
    (pin) => pin.assetType === "closing_conversation",
  );
  const enabled = closingPins.length > 0;

  // Candidates need the catalog, and only the catalog can say there are none:
  // `null` here is "not known", never "nothing to adopt".
  const candidates = catalog.isSuccess
    ? (
        sectionAdoptionCandidates({
          section: "closing",
          catalog: catalog.data.items,
          pins,
        }) ?? []
      ).map((asset) => ({
        asset,
        // `held: false` by construction: a candidate is an asset this project
        // does not pin, so this is an ADD and never a move.
        refusal: adoptionRefusal("pin", {
          asset,
          lock: adoptionLock(asset, undefined),
          held: false,
          missingWriteReason: write.reason,
          policyUnread,
        }),
      }))
    : null;

  // LOVABLE'S TOP-RIGHT ACTION, on the shell #909 published for it. The label is
  // the shared one: this is the same picker, over the same candidates, opening
  // the same confirmation.
  const adoptAction = (
    <AdoptFromLibraryAction
      sectionLabel={sectionLabel}
      candidates={candidates}
      onDecide={setDecision}
      // THE LANE, so the picker's own sentence describes the write it opens
      // onto. Derived from the section's adoptable families rather than named
      // here, exactly as `SectionAdoptionPanel` derives it — a literal would be
      // a second statement of which lane this family takes, free to disagree
      // with `activationLaneFor`, which is the one the confirmation dispatches
      // on.
      activation={activationLaneForSection("closing")}
    />
  );

  const dialog = decision ? (
    <AdoptionDecisionDialog
      projectId={projectId}
      decision={decision}
      onClose={() => setDecision(null)}
    />
  ) : null;

  // THE ENABLED/ADOPTED STATE, MADE EXPLICIT — the ticket's own words. Read from
  // the pin set rather than inferred from the presence of a row, and rendered
  // above the list where an author meets it before the details.
  const stageState = (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Badge tone={enabled ? "accent" : "neutral"}>
        {enabled ? "Closing enabled" : "Closing not enabled"}
      </Badge>
      <span className="text-muted-foreground">
        {enabled
          ? "This project runs a closing conversation after its final tracks: the session asks once whether the patient wants to add anything, accepts one reply and completes."
          : "This project runs no closing conversation. Sessions complete straight after the final tracks, which is the base pack's own configuration."}
      </span>
    </div>
  );

  const notices = (
    <>
      {controls}
      {stageState}
      {catalogNotice}
      <p className="rounded-md border border-border bg-muted px-4 py-3 text-sm text-muted-foreground">
        The closing conversation is the terminal stage: it runs after every final
        track and before the session ends. It is not a diagnostic pathway —
        adopting one never adds an Always-assessed or Dynamic selection, counts
        against no Dynamic limit, and changes no routing or Safety setting.
      </p>
    </>
  );

  if (!enabled) {
    return (
      <>
        <ProjectSectionPane
          sectionLabel={sectionLabel}
          primaryAction={adoptAction}
          controls={notices}
        >
          <EmptyState
            title="This project pins no closing conversation"
            description="Nothing from the shared clinical library's closing conversations is adopted into this project, so its sessions end as soon as the final tracks finish. Adopt one to enable the closing stage; an exact version that was removed earlier can be pinned again from the same list."
          />
        </ProjectSectionPane>
        {dialog}
      </>
    );
  }

  return (
    <>
      <ProjectSectionPane
        sectionLabel={sectionLabel}
        primaryAction={adoptAction}
        controls={notices}
      >
        <p className="text-sm text-muted-foreground">
          The closing conversation below is described at the{" "}
          <strong>exact version this project pins</strong>, not at the shared
          library&apos;s current one — a pin names an immutable version, so a
          later publish never moves it.
        </p>

        {/*
          `AdoptedRow` rather than a row of this module's own: it is `/adoptions`'
          pinned row, it states the pinned version through `ReadOnlyField` so a
          locked adoption explains itself in ADR 0001's words, and it carries the
          two controls this destination needs — `Update <name> to <head>` where
          the library has moved past the pin, and `Unpin <name>`, which for this
          family is the disable. A clinician meeting the same decision twice
          meets the same sentence, because it is the same component.

          `suppliedSections` is empty, and truthfully so: that list comes from
          `adoptionProvenanceByAsset` over `FieldState.origin`, and no section
          projection serves closing fields, so there is no attribution to render.
          The row omits the line entirely rather than claiming anything.
        */}
        <ul className="space-y-3">
          {closingPins.map((pin) => {
            const asset = assetsById.get(pin.assetId);
            const lock = adoptionLock(asset, undefined);

            return (
              <AdoptedRow
                key={pin.assetId}
                pin={pin}
                asset={asset}
                lock={lock}
                suppliedSections={[]}
                // `held: true` on both: this row is a pin the project holds, so
                // the `pin` decision is a MOVE and `adoptionRefusal` refuses it
                // under a lock exactly as it refuses the removal.
                upgradeRefusal={
                  adoptionRefusal("pin", {
                    asset,
                    lock,
                    held: true,
                    missingWriteReason: write.reason,
                    policyUnread,
                  }) ?? catalogDecisionRefusal
                }
                unpinRefusal={
                  adoptionRefusal("unpin", {
                    asset,
                    lock,
                    held: true,
                    missingWriteReason: write.reason,
                    // NEVER the policy on a removal: the un-pin is the recovery
                    // path out of every degraded state (#612), and for this
                    // family it is also how a closing conversation is disabled.
                    policyUnread: false,
                  }) ?? catalogDecisionRefusal
                }
                onDecide={setDecision}
              />
            );
          })}
        </ul>
      </ProjectSectionPane>
      {dialog}
    </>
  );
}
